import { useState } from 'react';
import { Link } from 'react-router-dom';
import { postSchoolApplication } from '@/api/public';
import { ArrowLeft, ArrowRight, Building2, UserCircle, CheckCircle2, AlertCircle, ShieldCheck, FileUp, X } from 'lucide-react';

const STATES = ['Abia', 'Adamawa', 'Akwa Ibom', 'Anambra', 'Bauchi', 'Bayelsa', 'Benue', 'Borno', 'Cross River', 'Delta', 'Ebonyi', 'Edo', 'Ekiti', 'Enugu', 'FCT Abuja', 'Gombe', 'Imo', 'Jigawa', 'Kaduna', 'Kano', 'Katsina', 'Kebbi', 'Kogi', 'Kwara', 'Lagos', 'Nasarawa', 'Niger', 'Ogun', 'Ondo', 'Osun', 'Oyo', 'Plateau', 'Rivers', 'Sokoto', 'Taraba', 'Yobe', 'Zamfara'];
const LEVELS = [['creche', 'Crèche'], ['nursery', 'Nursery'], ['primary', 'Primary'], ['junior_secondary', 'Junior secondary'], ['senior_secondary', 'Senior secondary']] as const;
const DOCS: { key: string; label: string; hint: string; required?: boolean; accept: string }[] = [
  { key: 'owner_id', label: "Owner's ID", hint: "NIN slip, international passport, driver's licence or voter's card", required: true, accept: '.pdf,.jpg,.jpeg,.png' },
  { key: 'cac_certificate', label: 'CAC certificate', hint: 'If the school is registered with CAC', accept: '.pdf,.jpg,.jpeg,.png' },
  { key: 'approval_letter', label: 'Ministry of Education approval', hint: 'State approval or registration letter', accept: '.pdf,.jpg,.jpeg,.png' },
  { key: 'proof_of_address', label: 'Proof of address', hint: 'Utility bill or tenancy for the school premises', accept: '.pdf,.jpg,.jpeg,.png' },
  { key: 'logo', label: 'School logo', hint: 'PNG or JPG — used on report sheets and receipts', accept: '.png,.jpg,.jpeg,.webp' },
];
const inputCls = 'w-full border border-slate-300 rounded-lg p-3 text-sm focus:border-blue-500 focus:ring-2 focus:ring-blue-200 outline-none bg-white';

export function SchoolApplyPage() {
  const [step, setStep] = useState<1 | 2 | 3 | 4 | 5>(1);
  const [files, setFiles] = useState<Record<string, File | null>>({});
  const [levels, setLevels] = useState<string[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [reference, setReference] = useState<string | null>(null);

  const [form, setForm] = useState({
    school_name: '',
    student_estimate: '',
    address: '', city: '', lga: '', state: '', school_type: 'private', cac_number: '', year_founded: '', website: '', owner_role: 'Proprietor', heard_from: '',
    owner_name: '',
    owner_email: '',
    owner_phone: '',
    password: '',
    password_confirmation: '',
  });

  const set = (k: keyof typeof form, v: string) => setForm({ ...form, [k]: v });

  const validateStep1 = () => {
    if (!form.school_name.trim()) return 'School name is required.';
    if (!form.address.trim()) return 'School address is required.';
    if (!form.state) return 'Choose the state.';
    if (!levels.length) return 'Tick the levels the school runs.';
    return null;
  };

  const validateStep2 = () => {
    if (!form.owner_name.trim()) return 'Your full name is required.';
    if (!form.owner_email.trim() || !/^\S+@\S+\.\S+$/.test(form.owner_email)) return 'A valid email is required.';
    if (!form.owner_phone.trim()) return 'Phone number is required.';
    if (form.password.length < 8) return 'Password must be at least 8 characters.';
    if (form.password !== form.password_confirmation) return 'Passwords do not match.';
    return null;
  };

  const next = () => {
    setError(null);
    if (step === 1) {
      const err = validateStep1();
      if (err) return setError(err);
      setStep(2);
    } else if (step === 2) {
      const err = validateStep2();
      if (err) return setError(err);
      setStep(3);
    } else if (step === 3) {
      if (!files.owner_id) return setError("Upload the owner's ID to continue.");
      const big = Object.values(files).find((f) => f && f.size > 5 * 1024 * 1024);
      if (big) return setError(`${big.name} is larger than 5 MB.`);
      setStep(4);
    }
  };

  const submit = async () => {
    setError(null);
    setSubmitting(true);
    try {
      const body = new FormData();
      const add = (k: string, v: string) => { if (v.trim() !== '') body.append(k, v.trim()); };
      add('school_name', form.school_name); add('owner_name', form.owner_name); add('owner_email', form.owner_email.toLowerCase()); add('owner_phone', form.owner_phone);
      body.append('password', form.password); body.append('password_confirmation', form.password_confirmation);
      add('student_estimate', form.student_estimate);
      (['address', 'city', 'lga', 'state', 'school_type', 'cac_number', 'year_founded', 'website', 'owner_role', 'heard_from'] as const).forEach((k) => add(k, form[k]));
      levels.forEach((l) => body.append('levels[]', l));
      Object.entries(files).forEach(([k, f]) => { if (f) body.append(`documents[${k}]`, f); });
      const res = await postSchoolApplication(body);
      const ref = res?.data?.data?.reference ?? res?.data?.reference;
      setReference(ref ?? 'submitted');
      setStep(5);
    } catch (e: any) {
      const msg = e?.response?.data?.message
        ?? (e?.response?.data?.errors && Object.values(e.response.data.errors).flat().join(' · '))
        ?? 'Could not submit application.';
      setError(String(msg));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-blue-50">
      <div className="max-w-3xl mx-auto px-4 py-8 md:py-16">
        <div className="mb-8 text-center">
          <Link to="/" className="inline-flex items-center gap-2 text-sm font-semibold text-slate-500 hover:text-slate-900 mb-4">
            <ArrowLeft className="w-4 h-4" /> Back to home
          </Link>
          <h1 className="text-3xl md:text-4xl font-extrabold text-slate-900">Bring your school online</h1>
          <p className="text-slate-600 mt-2 max-w-xl mx-auto">
            Apply to use OJP School App. Our team reviews every application and you'll hear back within 24–48 hours.
          </p>
        </div>

        {/* Progress */}
        {step < 5 && (
          <div className="flex items-center justify-center gap-2 mb-8">
            {[1, 2, 3, 4].map((n) => (
              <div key={n} className="flex items-center gap-2">
                <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold ${step >= n ? 'bg-[#0f172a] text-white' : 'bg-slate-200 text-slate-500'}`}>{n}</div>
                {n < 4 && <div className={`w-8 sm:w-12 h-1 rounded ${step > n ? 'bg-[#0f172a]' : 'bg-slate-200'}`} />}
              </div>
            ))}
          </div>
        )}

        <div className="bg-white rounded-2xl shadow-lg border border-slate-200 p-6 md:p-10">
          {error && (
            <div className="mb-6 p-3 bg-red-50 border border-red-200 rounded-lg flex items-start gap-2">
              <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
              <p className="text-sm text-red-800">{error}</p>
            </div>
          )}

          {step === 1 && (
            <div className="space-y-5">
              <div className="flex items-center gap-2 mb-4">
                <Building2 className="w-5 h-5 text-blue-600" />
                <h2 className="text-lg font-bold text-slate-900">About your school</h2>
              </div>
              <div>
                <label className="block text-sm font-semibold text-slate-700 mb-1">School name *</label>
                <input value={form.school_name} onChange={(e) => set('school_name', e.target.value)}
                  className="w-full border border-slate-300 rounded-lg p-3 text-sm focus:border-blue-500 focus:ring-2 focus:ring-blue-200 outline-none"
                  placeholder="e.g. Greenfield Academy" autoFocus />
              </div>
              <div>
                <label className="block text-sm font-semibold text-slate-700 mb-1">Estimated number of students</label>
                <input type="number" min={0} value={form.student_estimate} onChange={(e) => set('student_estimate', e.target.value)}
                  className="w-full border border-slate-300 rounded-lg p-3 text-sm focus:border-blue-500 focus:ring-2 focus:ring-blue-200 outline-none"
                  placeholder="e.g. 250" />
              </div>
              <div>
                <label className="block text-sm font-semibold text-slate-700 mb-1">School address *</label>
                <input value={form.address} onChange={(e) => set('address', e.target.value)} className={inputCls} placeholder="Street and number" />
              </div>
              <div className="grid sm:grid-cols-3 gap-3">
                <div><label className="block text-sm font-semibold text-slate-700 mb-1">Town / city</label><input value={form.city} onChange={(e) => set('city', e.target.value)} className={inputCls} /></div>
                <div><label className="block text-sm font-semibold text-slate-700 mb-1">LGA</label><input value={form.lga} onChange={(e) => set('lga', e.target.value)} className={inputCls} /></div>
                <div><label className="block text-sm font-semibold text-slate-700 mb-1">State *</label>
                  <select value={form.state} onChange={(e) => set('state', e.target.value)} className={inputCls}><option value="">Choose…</option>{STATES.map((x) => <option key={x}>{x}</option>)}</select></div>
              </div>
              <div>
                <label className="block text-sm font-semibold text-slate-700 mb-1">Levels the school runs *</label>
                <div className="flex flex-wrap gap-2">
                  {LEVELS.map(([k, l]) => (
                    <button type="button" key={k} onClick={() => setLevels(levels.includes(k) ? levels.filter((x) => x !== k) : [...levels, k])}
                      className={`px-3 py-2 rounded-lg border text-sm font-semibold ${levels.includes(k) ? 'bg-[#0f172a] text-white border-[#0f172a]' : 'border-slate-300 text-slate-700'}`}>{l}</button>
                  ))}
                </div>
              </div>
              <div className="grid sm:grid-cols-3 gap-3">
                <div><label className="block text-sm font-semibold text-slate-700 mb-1">Ownership</label>
                  <select value={form.school_type} onChange={(e) => set('school_type', e.target.value)} className={inputCls}>
                    <option value="private">Private</option><option value="mission">Mission / faith</option><option value="public">Public</option><option value="international">International</option></select></div>
                <div><label className="block text-sm font-semibold text-slate-700 mb-1">CAC number</label><input value={form.cac_number} onChange={(e) => set('cac_number', e.target.value)} className={inputCls} placeholder="RC / BN" /></div>
                <div><label className="block text-sm font-semibold text-slate-700 mb-1">Year founded</label><input inputMode="numeric" value={form.year_founded} onChange={(e) => set('year_founded', e.target.value.replace(/\D/g, '').slice(0, 4))} className={inputCls} /></div>
              </div>
              <button onClick={next} className="w-full mt-2 py-3 bg-[#0f172a] text-white rounded-lg font-bold text-sm hover:bg-slate-800 inline-flex items-center justify-center gap-2">
                Continue <ArrowRight className="w-4 h-4" />
              </button>
            </div>
          )}

          {step === 2 && (
            <div className="space-y-5">
              <div className="flex items-center gap-2 mb-4">
                <UserCircle className="w-5 h-5 text-blue-600" />
                <h2 className="text-lg font-bold text-slate-900">Your details</h2>
              </div>
              <div>
                <label className="block text-sm font-semibold text-slate-700 mb-1">Your full name *</label>
                <input value={form.owner_name} onChange={(e) => set('owner_name', e.target.value)}
                  className="w-full border border-slate-300 rounded-lg p-3 text-sm focus:border-blue-500 focus:ring-2 focus:ring-blue-200 outline-none"
                  placeholder="e.g. Mrs Ada Okafor" autoFocus />
              </div>
              <div>
                <label className="block text-sm font-semibold text-slate-700 mb-1">Your role in the school</label>
                <select value={form.owner_role} onChange={(e) => set('owner_role', e.target.value)} className={inputCls}>
                  {['Proprietor', 'Proprietress', 'Director', 'Principal / Head teacher', 'Administrator'].map((r) => <option key={r}>{r}</option>)}
                </select>
              </div>
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-semibold text-slate-700 mb-1">Email *</label>
                  <input type="email" value={form.owner_email} onChange={(e) => set('owner_email', e.target.value)}
                    className="w-full border border-slate-300 rounded-lg p-3 text-sm focus:border-blue-500 focus:ring-2 focus:ring-blue-200 outline-none"
                    placeholder="owner@school.ng" />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-slate-700 mb-1">Phone *</label>
                  <input value={form.owner_phone} onChange={(e) => set('owner_phone', e.target.value)}
                    className="w-full border border-slate-300 rounded-lg p-3 text-sm focus:border-blue-500 focus:ring-2 focus:ring-blue-200 outline-none"
                    placeholder="0803 000 0000" />
                </div>
              </div>
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-semibold text-slate-700 mb-1">Password *</label>
                  <input type="password" value={form.password} onChange={(e) => set('password', e.target.value)}
                    className="w-full border border-slate-300 rounded-lg p-3 text-sm focus:border-blue-500 focus:ring-2 focus:ring-blue-200 outline-none"
                    placeholder="At least 8 characters" />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-slate-700 mb-1">Confirm password *</label>
                  <input type="password" value={form.password_confirmation} onChange={(e) => set('password_confirmation', e.target.value)}
                    className="w-full border border-slate-300 rounded-lg p-3 text-sm focus:border-blue-500 focus:ring-2 focus:ring-blue-200 outline-none"
                    placeholder="Repeat password" />
                </div>
              </div>
              <p className="text-xs text-slate-500 flex items-start gap-1.5">
                <ShieldCheck className="w-4 h-4 flex-shrink-0 mt-0.5 text-blue-500" />
                You'll sign in with this password once your school is approved. Make it strong — 8+ characters.
              </p>
              <div className="flex gap-3 pt-2">
                <button onClick={() => setStep(1)} className="flex-1 py-3 border border-slate-300 rounded-lg font-bold text-sm text-slate-700 hover:bg-slate-50">
                  Back
                </button>
                <button onClick={next} className="flex-1 py-3 bg-[#0f172a] text-white rounded-lg font-bold text-sm hover:bg-slate-800 inline-flex items-center justify-center gap-2">
                  Continue <ArrowRight className="w-4 h-4" />
                </button>
              </div>
            </div>
          )}

          {step === 3 && (
            <div className="space-y-4">
              <div className="flex items-center gap-2 mb-2">
                <FileUp className="w-5 h-5 text-blue-600" />
                <h2 className="text-lg font-bold text-slate-900">Documents</h2>
              </div>
              <p className="text-sm text-slate-500">These let our team verify the school quickly. PDF or photo, up to 5 MB each. Only the owner's ID is required now; the rest speed up approval.</p>
              {DOCS.map((d) => {
                const f = files[d.key];
                return (
                  <div key={d.key} className="border border-slate-200 rounded-xl p-3 flex flex-col sm:flex-row sm:items-center gap-2">
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-semibold text-slate-900">{d.label}{d.required && ' *'}</p>
                      <p className="text-xs text-slate-500">{f ? `${f.name} · ${Math.max(1, Math.round(f.size / 1024))} KB` : d.hint}</p>
                    </div>
                    {f ? (
                      <button type="button" onClick={() => setFiles({ ...files, [d.key]: null })} className="inline-flex items-center gap-1 text-xs font-semibold text-red-600"><X className="w-3.5 h-3.5" />Remove</button>
                    ) : (
                      <label className="inline-flex items-center justify-center gap-1.5 px-3 py-2 rounded-lg border border-slate-300 text-sm font-semibold cursor-pointer hover:bg-slate-50">
                        <FileUp className="w-4 h-4" />Choose file
                        <input type="file" accept={d.accept} className="hidden" onChange={(e) => { const x = e.target.files?.[0]; if (x) setFiles({ ...files, [d.key]: x }); e.target.value = ''; }} />
                      </label>
                    )}
                  </div>
                );
              })}
              <div>
                <label className="block text-sm font-semibold text-slate-700 mb-1">How did you hear about OJP? (optional)</label>
                <input value={form.heard_from} onChange={(e) => set('heard_from', e.target.value)} className={inputCls} />
              </div>
              <div className="flex gap-3 pt-2">
                <button onClick={() => setStep(2)} className="flex-1 py-3 border border-slate-300 rounded-lg font-bold text-sm text-slate-700 hover:bg-slate-50">Back</button>
                <button onClick={next} className="flex-1 py-3 bg-[#0f172a] text-white rounded-lg font-bold text-sm hover:bg-slate-800 inline-flex items-center justify-center gap-2">Continue <ArrowRight className="w-4 h-4" /></button>
              </div>
            </div>
          )}

          {step === 4 && (
            <div className="space-y-5">
              <h2 className="text-lg font-bold text-slate-900 mb-4">Review and submit</h2>
              <div className="bg-slate-50 rounded-xl p-5 space-y-3 text-sm border border-slate-200">
                <Row label="School" value={form.school_name} />
                {form.student_estimate && <Row label="Students" value={`≈ ${form.student_estimate}`} />}
                <Row label="Address" value={[form.address, form.city, form.lga, form.state].filter(Boolean).join(', ')} />
                <Row label="Levels" value={LEVELS.filter(([k]) => levels.includes(k)).map(([, l]) => l).join(', ')} />
                {form.cac_number && <Row label="CAC" value={form.cac_number} />}
                <Row label="Documents" value={`${Object.values(files).filter(Boolean).length} attached`} />
                <hr className="border-slate-200" />
                <Row label="Owner" value={form.owner_name} />
                <Row label="Email" value={form.owner_email} />
                <Row label="Phone" value={form.owner_phone} />
                <Row label="Password" value="••••••••  (as chosen)" />
              </div>
              <p className="text-xs text-slate-500">By submitting you agree to our terms. Our team will review your application within 24–48 hours.</p>
              <div className="flex gap-3 pt-2">
                <button onClick={() => setStep(3)} disabled={submitting} className="flex-1 py-3 border border-slate-300 rounded-lg font-bold text-sm text-slate-700 hover:bg-slate-50 disabled:opacity-50">
                  Back
                </button>
                <button onClick={submit} disabled={submitting} className="flex-1 py-3 bg-[#0f172a] text-white rounded-lg font-bold text-sm hover:bg-slate-800 disabled:opacity-50">
                  {submitting ? 'Submitting…' : 'Submit application'}
                </button>
              </div>
            </div>
          )}

          {step === 5 && (
            <div className="text-center py-8">
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <CheckCircle2 className="w-8 h-8 text-green-600" />
              </div>
              <h2 className="text-2xl font-bold text-slate-900 mb-2">Application received!</h2>
              <p className="text-slate-600 mb-6 max-w-md mx-auto">
                We've recorded your application. Our team will review it within 24–48 hours and send you an SMS with the next steps.
              </p>
              {reference && (
                <div className="inline-block bg-slate-100 rounded-lg px-6 py-3 mb-6">
                  <p className="text-xs text-slate-500 font-semibold uppercase">Your reference</p>
                  <p className="text-lg font-mono font-bold text-slate-900 mt-1">{reference}</p>
                </div>
              )}
              <div>
                <Link to="/login" className="inline-flex items-center gap-2 text-sm font-semibold text-blue-600 hover:text-blue-800">
                  Already approved? Sign in <ArrowRight className="w-4 h-4" />
                </Link>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function Row({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex justify-between">
      <span className="text-slate-500">{label}</span>
      <span className="font-semibold text-slate-900 text-right">{value}</span>
    </div>
  );
}
